import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Edit, Eye, Plus, Trash2 } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { Run } from "../../lib/supabase";
import { ENTITY_NAMES } from "../../constants/entities";
import {
  isRunCompleted,
  formatDaysOfWeek,
  getRunDateRange,
  type LabDate,
} from "../../utils/runs";
import toast from "react-hot-toast";

interface RunWithSeries extends Run {
  series?: {
    id: string;
    code: string;
    title: string;
  };
  lab_dates?: LabDate[];
}

interface RunsSectionProps {
  seriesId: string;
  runs: RunWithSeries[];
  isAdmin: boolean;
  onRunCreated: () => void;
  type: "current" | "past";
  title?: string;
}

const RunsSection: React.FC<RunsSectionProps> = ({
  seriesId,
  runs,
  isAdmin,
  onRunCreated,
  type,
  title,
}) => {
  const handleDeleteRun = async (runId: string) => {
    if (!isAdmin) return;

    if (
      window.confirm(
        `Are you sure you want to delete this ${ENTITY_NAMES.RUN}? This will also remove all student enrollments.`
      )
    ) {
      try {
        const { error } = await supabase.from("runs").delete().eq("id", runId);

        if (error) throw error;

        toast.success(`${ENTITY_NAMES.RUN} deleted successfully`);
        onRunCreated();
      } catch (error) {
        console.error("Error deleting run:", error);
        toast.error(`Failed to delete ${ENTITY_NAMES.RUN.toLowerCase()}`);
      }
    }
  };

  const formatTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const filteredRuns = runs.filter((run) => {
    const isCompleted = isRunCompleted(run);
    return type === "current" ? !isCompleted : isCompleted;
  });

  const showSeriesNumber = !seriesId; // Only show series number when not in SeriesDetail

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-main">
          {title ||
            (type === "current"
              ? `Active ${ENTITY_NAMES.RUN}s`
              : `Past ${ENTITY_NAMES.RUN}s`)}
        </h2>
        {isAdmin && type === "current" && seriesId && (
          <Link to={`/series/${seriesId}/runs/new`} className="btn btn-primary">
            <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
            Add {ENTITY_NAMES.RUN}
          </Link>
        )}
      </div>

      {filteredRuns.length === 0 ? (
        <p className="text-neutral-500">
          {type === "current"
            ? `No active ${ENTITY_NAMES.RUN}s found.`
            : `No past ${ENTITY_NAMES.RUN}s.`}
        </p>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Group
                  </th>
                  {showSeriesNumber && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Series
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredRuns.map((run) => (
                  <tr key={run.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                      <Link to={`/runs/${run.id}`} className="">
                        {ENTITY_NAMES.RUN} {run.name}
                      </Link>
                    </td>
                    {showSeriesNumber && run.series && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        <Link to={`/series/${run.series.id}`} className="">
                          {run.series.code}
                        </Link>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-neutral-500 mr-2 flex-shrink-0" />
                        <div>
                          <div>
                            <span className="text-sm text-neutral-500 mt-1">
                              {getRunDateRange(run)}
                            </span>
                            <br />
                            {formatDaysOfWeek(run.day_of_week)}
                            <br /> {formatTime(run.start_time)} -{" "}
                            {formatTime(run.end_time)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-neutral-500 mr-2 flex-shrink-0" />
                        {run.location || "No location"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 text-right">
                      <div className="flex justify-end space-x-2">
                        <Link
                          to={`/runs/${run.id}`}
                          className="icon-view"
                          title="View run details"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                        {isAdmin && (
                          <>
                            <Link
                              to={`/runs/${run.id}/edit`}
                              className="icon-edit"
                              title="Edit run"
                            >
                              <Edit className="h-5 w-5" />
                            </Link>
                            <button
                              onClick={() => handleDeleteRun(run.id)}
                              className="icon-delete"
                              title="Delete run"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

export default RunsSection;
